/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ActionReducerMapBuilder } from "@reduxjs/toolkit";
import { AuthenticationTypeV3 } from "@tivocorp/data-domain-lib";
import type { AuthConfigurationV3 } from "@tivocorp/data-domain-lib";

import { Log } from "../../../model/logging";
import { LocaleKeys } from "../../../model/locale";
import { localizedErrorFallback } from "../../../model/utility";
import {
  isIpAutoLoginFeatureEnabled,
  SIGN_IN_FALLBACK_ERROR_CODE
} from "../common";
import type { SignInState } from "../signIn.types";
import { AUTH_ORCHESTRATION_IP_AUTO_LOGIN } from "../signIn.types";
import { initializeSignIn } from "../thunks/authConfig.thunks";

const TAG: string = "signInSlice";

/**
 * Picks the tab that opens by default: the first entry that is not the
 * silent IP auto-login type, falling back to the first entry when every
 * config is silent.
 */
function resolveDefaultTab(configs: AuthConfigurationV3[]): string {
  const firstInteractive: AuthConfigurationV3 | undefined = configs.find(
    (c: AuthConfigurationV3): boolean =>
      c.authenticationType !== AuthenticationTypeV3.TivoIpAutoLoginIdp
  );
  return firstInteractive !== undefined
    ? firstInteractive.authenticationType
    : configs[0].authenticationType;
}

/**
 * Reducers for the auth-configuration fetch that opens every sign-in.
 */
export function registerAuthConfigReducers(
  builder: ActionReducerMapBuilder<SignInState>
): void {
  builder.addCase(initializeSignIn.pending, (state) => {
    state.isInitializing = true;
    state.isInitialized = false;
    state.initError = "";
    state.initErrorCode = "";
    state.initErrorServiceCode = "";
  });
  builder.addCase(initializeSignIn.fulfilled, (state, action) => {
    state.isInitializing = false;
    state.isInitialized = true;
    state.authConfigurations = action.payload;
    Log.d(
      TAG,
      "initializeSignIn.fulfilled — configs=" +
        String(action.payload.length) +
        " types=" +
        action.payload
          .map((c: AuthConfigurationV3): string => c.authenticationType)
          .join(",")
    );
    if (action.payload.length > 0) {
      // Pre-set orchestration status when a silent auth type is present
      // so SplashScreen holds navigation until resolveNextAuthStep runs.
      // Use "ip-auto-login" (not "resolving") to avoid triggering
      // Step 4b's fallback re-dispatch in AppInitializer. When the
      // IP auto-login feature flag is off the silent type is skipped by
      // resolveNextAuthStep, so there is nothing to hold navigation for.
      const hasSilentAuth: boolean =
        isIpAutoLoginFeatureEnabled() &&
        action.payload.some(
          (c: AuthConfigurationV3): boolean =>
            c.authenticationType === AuthenticationTypeV3.TivoIpAutoLoginIdp
        );
      if (hasSilentAuth) {
        state.authOrchestrationStatus = AUTH_ORCHESTRATION_IP_AUTO_LOGIN;
        Log.d(
          TAG,
          "initializeSignIn.fulfilled — set authOrchestrationStatus=ip-auto-login"
        );
      }

      state.activeTab = resolveDefaultTab(action.payload);
    }
  });
  builder.addCase(initializeSignIn.rejected, (state, action) => {
    state.isInitializing = false;
    state.isInitialized = false;
    // No payload means the thunk threw before its catch ran: still an error,
    // and still one the sign-in screen must show rather than render blank.
    if (action.payload !== undefined && action.payload !== null) {
      state.initError = action.payload.message;
      state.initErrorCode = action.payload.code;
      state.initErrorServiceCode = action.payload.serviceCode;
      return;
    }
    state.initError = localizedErrorFallback(
      LocaleKeys.SIGN_IN_INITIALIZATION_ERROR_MESSAGE
    );
    state.initErrorCode = SIGN_IN_FALLBACK_ERROR_CODE;
    state.initErrorServiceCode = "";
  });
}
